// MehrfachSelect.tsx — Dropdown mit MEHRFACHauswahl im REFINED-Look.
//
// Das Gegenstück zu `SearchableSelect` (Einfachauswahl): dieselben Feld-Styles
// (`useFormPrimitiveStyles`), derselbe Aufklapp-Mechanismus (absolut im Feld,
// kein Portal), dieselbe Tastaturbedienung. Damit steht es optisch neben einem
// `LabeledSelect`/`SearchableSelect` in einer Zeile, ohne dass man die zweite
// Bauart sieht.
//
// WARUM KEIN PORTAL wie in `BrancheMultiSelect`: Dort trägt das Panel 220
// Blätter und muss deshalb gemessen und bei Platzmangel hochgeklappt werden.
// Hier sind es höchstens 45 Zeilen. Vor allem hängt das Feld in der Kontaktakte
// in einer `FeldPraesenzHuelle`, die die Anwesenheit über focusin/focusout am
// HÜLLcontainer misst: Ein Panel an `document.body` läge außerhalb und meldete
// beim Öffnen ein Verlassen des Feldes, obwohl der Anwender gerade darin
// arbeitet. `SearchableSelect` (Nationalität, Rechtsform, Finanzamt) klappt in
// genau denselben Masken seit jeher absolut auf.
//
// DASS MEHRERE WERTE MÖGLICH SIND, muss man dem geschlossenen Feld ansehen
// (Anwender ohne Technik-Erfahrung): Das Label trägt deshalb immer den Zusatz
// „(mehrere möglich)", und jede Zeile im Panel hat ein Kästchen, das man
// ankreuzt — das Panel bleibt nach einem Klick offen.

import * as React from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { useFormPrimitiveStyles } from "../formPrimitives";
import { moveActive, norm } from "../../verantwortlichkeit/verantwortlichkeitDropdownLogik";
import { alleDokumente } from "../../windowManager/browserFenster/fensterRegister";

/** Eine wählbare Zeile. Der Wert ist die Katalog-id oder ein String-Identifier, nie das Label. */
export interface MehrfachOption<T extends string | number = number> {
  value: T;
  label: string;
  /** Kurzer Zusatz am Zeilenende, z. B. „(deaktiviert)". */
  hint?: string;
  /** Sichtbar, aber nicht wählbar (z. B. Rolle passt nicht zum Kontakttyp). */
  gesperrt?: boolean;
}

const useLocal = makeStyles({
  wrap: { position: "relative" },
  labelZusatz: { fontWeight: 400, color: REFINED.ink3, fontSize: "0.8rem" },
  trigger: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    width: "100%",
    boxSizing: "border-box",
    textAlign: "left",
    appearance: "none",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding("11px", "13px"),
    backgroundColor: REFINED.white,
    fontFamily: REFINED.fontUI,
    fontSize: "0.95rem",
    color: REFINED.ink,
    cursor: "pointer",
    ":hover": { ...shorthands.borderColor(REFINED.aub100) },
    ":focus-visible": {
      ...shorthands.borderColor(REFINED.aub),
      outlineStyle: "none",
      boxShadow: "0 0 0 3px rgba(125,33,105,0.13)",
    },
    ":disabled": { backgroundColor: REFINED.aub50, color: REFINED.ink3, cursor: "not-allowed" },
  },
  triggerOffen: { ...shorthands.borderColor(REFINED.aub) },
  triggerText: { flexGrow: 1, minWidth: 0, overflowX: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  // ink2 statt ink3: ink3 auf Weiß ergibt 2,89:1 und liegt damit unter den
  // 4,5:1 der WCAG-AA. Anders als beim Branche-Feld daneben ist „keine
  // ausgewählt" hier KEIN ::placeholder, sondern der normale Feldinhalt — und
  // der Hinweis darunter („Bitte zuerst eine Branche wählen.") ist genau der
  // Satz, der erklärt, warum das Feld nicht reagiert. Vom gewählten Wert
  // (REFINED.ink) bleibt ink2 deutlich unterscheidbar, der „nichts gewählt"-
  // Zustand also weiter auf einen Blick ablesbar.
  platzhalter: { color: REFINED.ink2 },
  chevron: { color: REFINED.ink3, flexShrink: 0, display: "inline-flex" },

  panel: {
    position: "absolute",
    zIndex: 40,
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    display: "flex",
    flexDirection: "column",
    maxHeight: "292px",
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    boxShadow: REFINED.cardShadow,
    overflowX: "hidden",
    overflowY: "hidden",
  },
  sucheWrap: {
    ...shorthands.padding("8px"),
    ...shorthands.borderBottom("1px", "solid", REFINED.line),
    flexShrink: 0,
  },
  suche: {
    width: "100%",
    boxSizing: "border-box",
    ...shorthands.padding("7px", "10px"),
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("7px"),
    fontFamily: REFINED.fontUI,
    fontSize: "0.86rem",
    color: REFINED.ink,
    outlineStyle: "none",
    ":focus": {
      ...shorthands.borderColor(REFINED.aub),
      boxShadow: "0 0 0 3px rgba(125,33,105,0.13)",
    },
  },
  liste: {
    overflowY: "auto",
    overflowX: "hidden",
    paddingTop: "4px",
    paddingBottom: "4px",
    flexGrow: 1,
    outlineStyle: "none",
  },
  optGesperrt: { opacity: 0.45, cursor: "not-allowed" },
  opt: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "9px",
    width: "100%",
    boxSizing: "border-box",
    textAlign: "left",
    appearance: "none",
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    ...shorthands.padding("7px", "12px"),
    fontFamily: REFINED.fontUI,
    fontSize: "0.9rem",
    color: REFINED.ink,
    cursor: "pointer",
  },
  optAktiv: { backgroundColor: REFINED.aub50 },
  optGewaehlt: { fontWeight: 700, color: REFINED.aub },
  // Ein Kästchen statt eines bloßen Hakens: Das leere Kästchen sagt, dass hier
  // noch mehr angekreuzt werden darf.
  box: {
    width: "16px",
    height: "16px",
    marginTop: "2px",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("4px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
  },
  boxAn: { ...shorthands.borderColor(REFINED.aub), backgroundColor: REFINED.aub50 },
  optText: { flexGrow: 1, minWidth: 0, overflowWrap: "anywhere" },
  optAlt: { marginLeft: "6px", fontSize: "0.72rem", fontWeight: 600, color: REFINED.ink3, whiteSpace: "nowrap" },
  leer: { ...shorthands.padding("12px"), fontFamily: REFINED.fontUI, fontSize: "0.86rem", color: REFINED.ink3 },
  hinweis: { fontFamily: REFINED.fontUI, fontSize: "0.78rem", color: REFINED.ink2 },
  // Fußzeile mit „Fertig" (28.09.2026): Das Panel bleibt nach jedem Ankreuzen
  // offen — ohne sichtbaren Abschluss wussten Anwender nicht, wie sie „fertig"
  // sagen (Klick daneben ist für Nicht-Techniker keine erkennbare Aktion).
  fuss: {
    display: "flex",
    justifyContent: "flex-end",
    flexShrink: 0,
    ...shorthands.padding("6px", "8px"),
    ...shorthands.borderTop("1px", "solid", REFINED.line),
  },
  fertig: {
    appearance: "none",
    ...shorthands.border("0"),
    ...shorthands.borderRadius("7px"),
    ...shorthands.padding("6px", "14px"),
    backgroundColor: REFINED.aub,
    color: REFINED.white,
    fontFamily: REFINED.fontUI,
    fontSize: "0.86rem",
    fontWeight: 700,
    cursor: "pointer",
    ":focus-visible": { outlineStyle: "none", boxShadow: "0 0 0 3px rgba(125,33,105,0.25)" },
  },
});

const Chevron: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 6l4 4 4-4" />
  </svg>
);

const Haken: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3.2 8.4l3 3 6.6-7" />
  </svg>
);

/**
 * Zweite Suchform: die deutsche UMSCHREIBUNG (ä→ae, ö→oe, ü→ue, ß→ss).
 *
 * `norm` (Verantwortlichkeits-Dropdown) zerlegt per NFD nur die kombinierenden
 * Zeichen — daraus wird „ü" zu „u" und „ß" bleibt „ß". Wer auf Windows/Edge
 * „strassen" oder „geruestbau" tippt (verbreitet, weil beides ohne Sondertaste
 * geht), fand damit weder „Tief-, Straßen- & Kanalbau" noch „Gerüstbau" und sah
 * „Keine Branchengruppe gefunden.".
 */
export function normUmschrift(v: string): string {
  return (v || "")
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss");
}

/**
 * Suchform je Zeile: Mehrwortsuche über Label UND Zusatz (`hint`), BEIDE
 * Normalisierungen — die diakritikafreie („gerustbau") und die deutsche
 * Umschrift („geruestbau"). Jedes Wort muss in einer der beiden Formen
 * vorkommen; so findet auch „gerüstbau" selbst weiterhin seine Zeile.
 *
 * Der Zusatz zählt mit, weil die FO-Auswahl der Kontaktanlage dort das Kürzel
 * trägt und im Haus nach „MK“ statt nach dem Namen gesucht wird (T-164,
 * 03.10.2026). Trifft die Eingabe einen Zusatz exakt, steht diese Zeile oben.
 *
 * `filterOptions` aus dem Verantwortlichkeits-Dropdown passt nicht: Deren
 * `VerantOption` verlangt ein Pflichtfeld `bereich`, das es hier nicht gibt.
 */
function optionenFiltern<T extends string | number = number>(
  options: MehrfachOption<T>[],
  query: string,
): MehrfachOption<T>[] {
  const roh = query.trim();
  if (!roh) return options;
  const teile = roh.split(/\s+/).filter(Boolean);
  const treffer = options.filter((o) => {
    const text = `${o.label} ${o.hint ?? ""}`;
    const heu = `${norm(text)} ${normUmschrift(text)}`;
    return teile.every((t) => heu.includes(norm(t)) || heu.includes(normUmschrift(t)));
  });
  const exakt = (o: MehrfachOption<T>) => !!o.hint && norm(o.hint.trim()) === norm(roh);
  return [...treffer.filter(exakt), ...treffer.filter((o) => !exakt(o))];
}

export function MehrfachSelect<T extends string | number = number>({
  label,
  name,
  value,
  options,
  onToggle,
  beschriftung,
  disabled = false,
  hinweis,
  sucheAb = 10,
  sucheLabel,
  emptyText = "Keine Treffer",
  labelZusatz = "(mehrere möglich)",
  fertigKnopf = false,
}: {
  label: string;
  /** DOM-id des geschlossenen Feldes; auch Präfix von Listbox- und Zeilen-ids. */
  name: string;
  /** Die gewählten ids — die REIHENFOLGE gehört dem Aufrufer. */
  value: T[];
  options: MehrfachOption<T>[];
  /** Eine Zeile an-/abwählen. Das Panel bleibt offen (Mehrfachauswahl). */
  onToggle: (id: T) => void;
  /** Text des geschlossenen Feldes („keine ausgewählt" / Name / „n ausgewählt"). */
  beschriftung: string;
  disabled?: boolean;
  /** Kleiner Text unter dem Feld, z. B. warum es gerade gesperrt ist. */
  hinweis?: string;
  /** Ab wie vielen Zeilen ein Suchfeld erscheint. */
  sucheAb?: number;
  sucheLabel?: string;
  emptyText?: string;
  labelZusatz?: string;
  /** Knopf „Fertig" am Fuß des geöffneten Panels, der es schließt. */
  fertigKnopf?: boolean;
}) {
  const s = useFormPrimitiveStyles();
  const l = useLocal();
  const [offen, setOffen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [active, setActive] = React.useState(-1);

  const wrapRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const sucheRef = React.useRef<HTMLInputElement>(null);
  const listeRef = React.useRef<HTMLDivElement>(null);
  const fertigRef = React.useRef<HTMLButtonElement>(null);

  const mitSuche = options.length >= sucheAb;
  const sichtbar = React.useMemo(() => optionenFiltern(options, query), [options, query]);
  const gewaehlt = React.useMemo(() => new Set(value), [value]);
  // `aria-controls` muss auf das Element mit role="listbox" zeigen, nicht auf
  // die Hülle darum: Ein Screenreader folgt der Referenz und fände sonst ein
  // Element ohne Rolle. Aus demselben Grund trägt jede Zeile eine id — nur so
  // kann `aria-activedescendant` die Pfeil-Markierung ansagen, während der
  // Fokus im Suchfeld bzw. auf der Liste bleibt.
  const listboxId = `${name}-listbox`;
  const optionId = (i: number) => `${name}-opt-${i}`;
  const aktiveOptionId =
    active >= 0 && active < sichtbar.length ? optionId(active) : undefined;

  // Ein gesperrtes Feld darf nicht offen stehen bleiben: Wechselt die Branche
  // zurück auf „keine ausgewählt", während das Panel offen ist, zeigte es sonst
  // eine leere Liste über einem gesperrten Feld.
  React.useEffect(() => {
    if (disabled) setOffen(false);
  }, [disabled]);

  React.useEffect(() => {
    if (!offen) return;
    // Der Fokus wandert INS Panel — sonst löste Enter/Leertaste den Trigger
    // erneut aus und schlösse gerade wieder, was man öffnen wollte.
    if (mitSuche) sucheRef.current?.focus();
    else listeRef.current?.focus();
  }, [offen, mitSuche]);

  React.useEffect(() => {
    if (!offen) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setOffen(false);
    };
    alleDokumente.addEventListener("pointerdown", onDown);
    return () => alleDokumente.removeEventListener("pointerdown", onDown);
  }, [offen]);

  // scrollIntoView gibt es in jsdom nicht — ein fehlender Scroll darf die
  // Auswahl nie zum Absturz bringen.
  React.useEffect(() => {
    if (!offen || active < 0) return;
    listeRef.current
      ?.querySelector<HTMLElement>(`[data-idx="${active}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [offen, active]);

  const schliessen = (fokusZurueck = true) => {
    setOffen(false);
    setQuery("");
    setActive(-1);
    if (fokusZurueck) triggerRef.current?.focus();
  };

  const oeffnen = () => {
    setQuery("");
    setActive(-1);
    setOffen(true);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!offen) {
      // Pfeil ab/auf öffnet das GESCHLOSSENE Feld — genau wie das Branche-Feld
      // unmittelbar daneben (`SearchableSelect`) und wie ein natives <select>
      // in Edge. Ohne das verhielten sich zwei Felder derselben Zeile für
      // Tastaturnutzer unterschiedlich. Enter/Leertaste braucht es hier nicht:
      // Der Trigger ist ein <button> und öffnet über seine native Aktivierung.
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        oeffnen();
      }
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => moveActive(a, e.key === "ArrowDown" ? 1 : -1, sichtbar.length));
      return;
    }
    // Leertaste ist in einer Mehrfach-Listbox die Standardtaste zum Ankreuzen.
    // IM SUCHFELD bleibt sie ein Leerzeichen — sonst ließe sich „tief strassen"
    // nicht mehr tippen.
    const imSuchfeld = e.target === sucheRef.current;
    // Auf dem „Fertig"-Knopf lösen Enter/Leertaste den Knopf selbst aus
    // (natives Klick-Verhalten) statt eine Zeile anzukreuzen.
    if ((e.key === "Enter" || e.key === " ") && e.target === fertigRef.current) return;
    if (e.key === "Enter" || (e.key === " " && !imSuchfeld)) {
      // preventDefault auch ohne Ziel: Ein Enter, während ein Menü offen steht,
      // darf nie das Formular absenden. Ohne Pfeiltaste gibt es allerdings
      // keine markierte Zeile (`active === -1`) — dann passiert bewusst nichts,
      // statt blind die erste Zeile anzukreuzen.
      e.preventDefault();
      const ziel = active >= 0 ? sichtbar[active] : sichtbar.length === 1 ? sichtbar[0] : undefined;
      if (ziel && !ziel.gesperrt) onToggle(ziel.value);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      schliessen();
    }
  };

  return (
    <div className={s.field}>
      <label className={s.label} htmlFor={name}>
        {label} {labelZusatz ? <span className={l.labelZusatz}>{labelZusatz}</span> : null}
      </label>
      <div
        className={l.wrap}
        ref={wrapRef}
        onKeyDown={onKey}
        // Tab aus dem Panel heraus schließt es — sonst bliebe ein Menü offen
        // stehen, während der Fokus schon im nächsten Feld ist.
        onBlur={(e) => {
          if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) setOffen(false);
        }}
      >
        <button
          type="button"
          id={name}
          ref={triggerRef}
          disabled={disabled}
          role="combobox"
          aria-expanded={offen}
          aria-haspopup="listbox"
          aria-controls={offen ? listboxId : undefined}
          className={mergeClasses(l.trigger, offen && l.triggerOffen)}
          onClick={() => (offen ? schliessen(false) : oeffnen())}
        >
          <span className={mergeClasses(l.triggerText, value.length === 0 && l.platzhalter)}>
            {beschriftung}
          </span>
          <span className={l.chevron}>
            <Chevron />
          </span>
        </button>

        {offen && (
          <div className={l.panel}>
            {mitSuche && (
              <div className={l.sucheWrap}>
                <input
                  ref={sucheRef}
                  className={l.suche}
                  value={query}
                  placeholder="Suchen …"
                  autoComplete="off"
                  role="combobox"
                  aria-expanded
                  aria-autocomplete="list"
                  aria-controls={listboxId}
                  // Der Fokus liegt beim Tippen HIER, die Pfeil-Markierung
                  // wandert aber in der Liste. Ohne diese Zeile sagt NVDA/JAWS
                  // nicht an, auf welcher Branchengruppe man gerade steht.
                  aria-activedescendant={aktiveOptionId}
                  aria-label={sucheLabel ?? `${label} durchsuchen`}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActive(-1);
                  }}
                />
              </div>
            )}
            <div
              className={l.liste}
              ref={listeRef}
              id={listboxId}
              tabIndex={-1}
              role="listbox"
              aria-multiselectable
              aria-label={label}
              // Ohne Suchfeld trägt die Liste selbst den Fokus.
              aria-activedescendant={!mitSuche ? aktiveOptionId : undefined}
            >
              {sichtbar.length === 0 ? (
                <div className={l.leer}>{emptyText}</div>
              ) : (
                sichtbar.map((o, i) => {
                  const an = gewaehlt.has(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      role="option"
                      id={optionId(i)}
                      aria-selected={an}
                      aria-disabled={o.gesperrt || undefined}
                      data-idx={i}
                      // Nach dem ARIA-Listbox-Muster darf eine Option KEIN
                      // eigener Tab-Stopp sein: „Dienstleistung & Freie Berufe"
                      // hat 45 Gruppen — ohne das musste man 46-mal Tab
                      // drücken, um zum nächsten Formularfeld zu kommen. Der
                      // Fokus bleibt im Suchfeld bzw. auf der Liste, gesteuert
                      // wird mit den Pfeiltasten (`aria-activedescendant`).
                      tabIndex={-1}
                      className={mergeClasses(l.opt, i === active && l.optAktiv, an && l.optGewaehlt, o.gesperrt && l.optGesperrt)}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => !o.gesperrt && onToggle(o.value)}
                    >
                      <span className={mergeClasses(l.box, an && l.boxAn)} aria-hidden>
                        {an && <Haken />}
                      </span>
                      <span className={l.optText}>
                        {o.label}
                        {o.hint && <span className={l.optAlt}>{o.hint}</span>}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
            {fertigKnopf && (
              <div className={l.fuss}>
                <button type="button" ref={fertigRef} className={l.fertig} onClick={() => schliessen()}>
                  Fertig
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {hinweis && <div className={l.hinweis}>{hinweis}</div>}
    </div>
  );
}

export default MehrfachSelect;
